import React, { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { detect } from 'detect-browser'
import { Animation, Container, Content, CustomProvider as IntlProvider } from 'rsuite'
import dynamic from 'next/dynamic'
import en_GB from 'rsuite/locales/en_GB'
import fi_FI from 'rsuite/locales/fi_FI'
import sv_SE from 'rsuite/locales/sv_SE'
import de_DE from 'rsuite/locales/de_DE'
import useTranslation from 'next-translate/useTranslation'
import { Crisp } from 'crisp-sdk-web'

import Onboarding from '../Onboarding/Onboarding'
import CookieBanner from '../CookieBanner/CookieBanner'
import MobileBlocker from '../MobileBlocker'
import InvalidSessionModal from '../Modals/InvalidSessionModal'
import CheckoutFooter from './Footer/CheckoutFooter'
import CheckoutHeader from './Header/CheckoutHeader'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import { useAuth } from '../../services/useAuth'
import { isSuperUser, isSupplier } from '../../lib/supplier'
import useIsOnMobile from '../../services/useIsOnMobile'
import { MODAL_HASHES } from '../../utils/constants'
import { HeaderContext } from './Header/hooks/headerContext'
import Header from './Header/Header'
import NotificationHeader from './Header/NotificationHeader'
import MobileHeader from './Header/MobileHeader'
import Footer from './Footer/Footer'
import useIsUrlHashIncluded from '../../services/useIsUrlHashIncluded'
import ChangeLanguageModal from '../Modals/ChangeLanguageModal'
import useLoginModal from '../../services/useLoginModal'
import UnsupportedBrowser from '../Modals/UnsupportedBrowserModal'
import { checkIsBrowserSupported } from '../../utils/util'
import SimpleHeader from './Header/SimpleHeader'
import { StrapiFooterResp } from '../../utils/types/strapi'
import withImpersonation from '../Impersonation/withImpersonation'
import { createCookie, deleteCookie } from '../../utils/cookies'
import { useCustomer } from '../BuyerProfile/Account/CustomerProvider'
import FooterContactUsSection from './Footer/FooterContactUsSection'
import useBuyerOnboardingModal from '../hooks/useBuyerOnboardingModal'
import { useCart } from '../Cart/CartProvider'
import { selectCartProductsUnique, selectTotalCartQuantity } from '../Cart/selectors'
import { CartProductWithQuantities } from '../../utils/types/Cart'
import { Product } from '../../utils/types/Product'
import { isProductInclude } from '../../utils/types/guards/Order'
import contactEvent from '../../utils/analytics/contactEvent'
import SupplierVibeIconCarousel from '../Carousels/SupplierVibeIconCarousel'

import mobileContactStyles from '../../styles/MobileBlocker.module.less'
import pageLayoutStyles from '../../styles/PageLayout.module.less'
import headerStyles from '../../styles/Header.module.less'

// Not included in initial JS chunk for performance
const ContactModal = dynamic(() => import('../Modals/ContactModal'))
const RFQModal = dynamic(() => import('../Modals/ModalFlows/QuoteRequest/QuoteRequestModal'))
const SampleRequestModal = dynamic(() => import('../Modals/ModalFlows/SampleRequest/SampleRequestModal'))
const DocumentRequestModal = dynamic(() => import('../Modals/ModalFlows/DocumentRequest/DocumentRequestModal'))
const LoginModal = dynamic(() => import('../Modals/LoginModal'))
const BuyerOnboardingModal = dynamic(() => import('../Auth/BuyerOnboardingModal'))
const ImpersonationToolbar = dynamic(() => import('../Impersonation/Toolbar'))
const SelectCustomerTypeModal = dynamic(() => import('../Modals/SelectCustomerTypeModal'))
const CartItemsModal = dynamic(() => import('../CartItemsModal/CartItemsModal'))
interface LayoutProps {
  children?: ReactNode
  // Workaround to include footer content via SSG
  // TODO: Implement SSG footer globally to Layout component in Next.js 13+
  footerContentResp?: StrapiFooterResp
}

enum QuantityCount {
  initialState = -1,
  tabChangeState = -2,
}

const Layout: FC<LayoutProps> = ({ children, footerContentResp }) => {
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)
  const [isBrowserUnSupported, setIsBrowserUnSupported] = useState(false)
  const [isCartModalOpen, setIsCartModalOpen] = useState(false)
  const [recentlyAddedProducts, setRecentlyAddedProducts] = useState<
  CartProductWithQuantities[]
  >([])

  const currentQuantityCount = useRef(QuantityCount.initialState)
  const currentCartProductsInfo = useRef<CartProductWithQuantities[] | null>(null)
  const {
    cartState: { cart, isLoading },
    modifiedCart,
  } = useCart()
  const { user, refresh, isImpersonated } = useAuth()
  const { customerType: cookieCustomerType, updateCustomerType } = useCustomer()
  const { locale: countryAndLocaleString, pathname, query, asPath } = useRouter()
  const isOnMobile = useIsOnMobile()
  const { t } = useTranslation('common')

  const layoutContainer = useRef<HTMLDivElement>(null)
  // Used for components that need header / top header refs via prop drilling or context
  const notificationHeaderRef = useRef<HTMLDivElement>(null)
  const mainHeaderRef = useRef<HTMLDivElement>(null)

  const isOnCheckoutSummaryPage = asPath.includes(t('url:summary'))
  // Check if the user is currently during the checkout process, before the checkout summary
  const isOnCheckoutStep = pathname === '/checkout/[step]' && !isOnCheckoutSummaryPage

  const quantityCount = cart ? selectTotalCartQuantity(cart) : 0
  const { shareParams } = query
  const isOnCartPage = pathname.startsWith('/cart')
  const showSimpleHeader = pathname === '/authorize'

  const { locale } = getCountryAndLocaleStrings(countryAndLocaleString)
  const browser = detect()
  const products = cart ? selectCartProductsUnique(cart) : []

  const {
    CONTACT_URL_HASH,
    RFQ_URL_HASH,
    SAMPLE_REQUEST_URL_HASH,
    DOCUMENT_REQUEST_URL_HASH,
    SELECT_CUSTOMER_TYPE_URL_HASH,
  } = MODAL_HASHES

  const renderContactModal = useIsUrlHashIncluded(CONTACT_URL_HASH)
  const renderRFQModal = useIsUrlHashIncluded(RFQ_URL_HASH)
  const renderSampleRequestModal = useIsUrlHashIncluded(SAMPLE_REQUEST_URL_HASH)
  const renderDocumentRequestModal = useIsUrlHashIncluded(DOCUMENT_REQUEST_URL_HASH)
  const renderSelectCustomerTypeModal = useIsUrlHashIncluded(SELECT_CUSTOMER_TYPE_URL_HASH)
  const { shouldShowLoginModal } = useLoginModal()
  const { shouldShowOnboardingModal } = useBuyerOnboardingModal()

  const updatedCartProducts: CartProductWithQuantities[] | undefined = modifiedCart?.included
    ?.filter(isProductInclude)
    ?.reduce<{ product: Product, quantity: Record<string, number> }[]>((acc, item) => {
    if (!item.product || item.attributes['order.product.quantity'] === undefined) {
      return acc
    }
    const existingProduct = acc.find((prod) => prod.product.id === item.product!.id)
    const variantLabel = item.attributes.attribute?.[0]['order.product.attribute.name'] || ''
    const quantity = item.attributes['order.product.quantity']

    if (existingProduct) {
      existingProduct.quantity[variantLabel] = (
        existingProduct.quantity[variantLabel] || 0
      ) + quantity
    } else {
      acc.push({
        product: item.product,
        quantity: { [variantLabel]: quantity },
      })
    }
    return acc
  }, [])

  const calculateNewlyAddedProducts = () => updatedCartProducts?.reduce((acc, updatedProduct) => {
    const previousProduct = currentCartProductsInfo.current?.find(
      (prevProduct) => prevProduct.product.id === updatedProduct.product.id,
    )

    // If the product is newly added, add it to the list
    if (!previousProduct) {
      acc.push({ product: updatedProduct.product, quantity: updatedProduct.quantity })
      return acc
    }

    // Calculate the quantity differences for existing products and their variants
    const updatedQuantities = Object.entries(updatedProduct.quantity).reduce(
      (variantAcc, [variant, quantity]) => {
        const previousQuantity = previousProduct.quantity[variant] || 0
        const difference = quantity - previousQuantity
        if (difference > 0) {
          return { ...variantAcc, [variant]: difference }
        }
        return variantAcc
      },
      {} as Record<string, number>,
    )

    if (Object.keys(updatedQuantities).length > 0) {
      acc.push({ product: updatedProduct.product, quantity: updatedQuantities })
    }

    return acc
  }, [] as { product: Product, quantity: Record<string, number> }[]) || []

  let rsuiteLocale
  switch (locale) {
    case 'fi': {
      rsuiteLocale = fi_FI
      break
    }
    case 'sv': {
      rsuiteLocale = sv_SE
      break
    }
    case 'de': {
      rsuiteLocale = de_DE
      break
    }
    default: {
      rsuiteLocale = en_GB
      break
    }
  }

  // Close the cart modal when the user clicks some product from the carousel
  useEffect(() => {
    if (asPath && isCartModalOpen) {
      setIsCartModalOpen(false)
    }
  }, [asPath])

  useEffect(() => {
    setIsBrowserUnSupported(!checkIsBrowserSupported(browser?.name, browser?.version))

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Set to -2 when the tab is changed
        currentQuantityCount.current = QuantityCount.tabChangeState
      }
    }

    // Listener for capturing tab visibility changes
    // so that modal popup not appear on every tab switch
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    if (!cart?.included) {
      return
    }

    if (currentQuantityCount.current === QuantityCount.tabChangeState && !isLoading) {
      // Reset to -1 when the tab is open again
      // so to preserve the added quantity count change
      currentQuantityCount.current = QuantityCount.initialState

      return
    }

    if (currentQuantityCount.current === QuantityCount.initialState && !isLoading) {
      currentQuantityCount.current = quantityCount
      currentCartProductsInfo.current = updatedCartProducts || null

      return
    }

    const hasTotalQuantityCountChanged = currentQuantityCount.current !== quantityCount

    if (!shareParams
      && !isOnCheckoutSummaryPage
      && !isLoading
      && hasTotalQuantityCountChanged) {
      currentQuantityCount.current = quantityCount
      setRecentlyAddedProducts(calculateNewlyAddedProducts()) // Save the changes made to the cart
      currentCartProductsInfo.current = updatedCartProducts || null
      if (!isOnCartPage && !shareParams && !isOnCheckoutSummaryPage) {
        setIsCartModalOpen(true)
      }
    }
  }, [products, isOnCartPage, quantityCount, updatedCartProducts])

  // Initialize Crisp chat
  useEffect(() => {
    Crisp.configure(process.env.NEXT_PUBLIC_CRISP_CHAT_ID || '', {
      locale,
    })

    if (user) {
      Crisp.user.setEmail(user.email)
      Crisp.user.setNickname(user.name)
    }

    Crisp.chat.onChatOpened(() => contactEvent(
      'chat_started',
      'chat',
    ))
  }, [user, locale])

  // Sync customer type between cookie and logged in user data
  useEffect(() => {
    const syncCustomerType = async () => {
      if (cookieCustomerType) {
        // Ensure old cookie with out-dated domain format is replaced with new value
        // TODO: Remove once all users have updated to the new cookie domain format – 1.1.2026

        // Thoroughly delete the cookie from all possible domain variations
        deleteCookie('customerType', null)

        // Create the cookie with the current standard domain format
        createCookie('customerType', cookieCustomerType)
      }

      // Continue with existing sync logic
      // If user doesn't have customer type, update it with the cookie value
      if (user && (user.customertype === '' || user.customertype === null) && cookieCustomerType) {
        await updateCustomerType(cookieCustomerType)
        refresh()
      } else if (user && user.customertype !== cookieCustomerType) {
        // If user has customer type, but it doesn't match the cookie value, update the cookie
        createCookie('customerType', user.customertype)
      } else if (!cookieCustomerType) {
        // If the cookie doesn't exist, create a default cookie value
        createCookie('customerType', 'home')
      }
    }

    if (!pathname.includes('/login-link')) {
      syncCustomerType()
    }
  }, [user?.email, user?.customertype])

  const renderHeaderComponent = () => {
    if (isOnCheckoutStep) {
      return <CheckoutHeader ref={mainHeaderRef} />
    }

    if (showSimpleHeader) {
      return <SimpleHeader ref={mainHeaderRef} />
    }

    return (
      <>
        <Header ref={mainHeaderRef} />
        <div
          className={`${headerStyles.header} ${pageLayoutStyles['mobile-header']}  ${isOnCartPage ? headerStyles['non-sticky-header'] : ''} hide-on-print`}
          ref={isOnMobile ? mainHeaderRef : undefined}
        >
          <MobileHeader />
        </div>
      </>

    )
  }

  const shouldShowImpersonationToolbar = !!user && (isSuperUser(user) || isImpersonated)

  return (
    <>
      {shouldShowImpersonationToolbar && (
        <Animation.Slide
          in={shouldShowImpersonationToolbar}
          placement="bottom"
        >
          {(props, ref) => (
            <div
              {...props}
              ref={ref}
              className={`${pageLayoutStyles['superuser-toolbar']} ${props.className}`}
            >
              <ImpersonationToolbar />
            </div>
          )}
        </Animation.Slide>
      )}
      <InvalidSessionModal />
      <HeaderContext.Provider value={mainHeaderRef}>
        <Container
          ref={layoutContainer}
          className={`${pageLayoutStyles['layout-container']} ${isLanguageModalOpen ? 'language-modal-open' : ''}`}
        >
          {!isOnCheckoutStep && !isOnMobile && <NotificationHeader ref={notificationHeaderRef} />}
          {renderHeaderComponent()}
          <MobileBlocker />
          <IntlProvider locale={rsuiteLocale}>
            <Content>
              {isSupplier(user)
                ? (
                  <Onboarding>
                    {children}
                  </Onboarding>
                )
                : (
                  <>
                    { children }
                  </>
                )}
              {/*
               The below dynamic modals can be used via the following hooks:
                - useContactModal
                - useRFQModal
                - useSampleRequestModal
              */}
              {renderContactModal && (
              <ContactModal
                staticBackdrop={isOnMobile}
                backdropClassName={isOnMobile ? mobileContactStyles['mobile-backdrop'] : ''}
              />
              )}
              {shouldShowLoginModal && <LoginModal />}
              {shouldShowOnboardingModal && <BuyerOnboardingModal />}
              {renderRFQModal && <RFQModal />}
              {renderSampleRequestModal && <SampleRequestModal />}
              {renderDocumentRequestModal && <DocumentRequestModal />}
              {renderSelectCustomerTypeModal && <SelectCustomerTypeModal />}
              {isCartModalOpen && modifiedCart?.included.length && recentlyAddedProducts.length && (
                <CartItemsModal
                  recentlyAddedProducts={recentlyAddedProducts}
                  onClose={() => setIsCartModalOpen(false)}
                  isOnMobile={isOnMobile}
                />
              )}
              <ChangeLanguageModal
                isOpen={isLanguageModalOpen}
                setIsOpen={setIsLanguageModalOpen}
                container={layoutContainer?.current || undefined}
                backdropClassName="language-modal-backdrop"
              />
            </Content>
          </IntlProvider>
          <FooterContactUsSection />

          {/* Add Supplier Vibe Icon Carousel to footer */}
          <div className={`${pageLayoutStyles['vibe-icons-section']}`}>
            <SupplierVibeIconCarousel
              className="container"
            />
          </div>

          {isOnCheckoutStep
            ? <CheckoutFooter />
            : <Footer footerContentResp={footerContentResp} />}
        </Container>
      </HeaderContext.Provider>

      {isBrowserUnSupported && <UnsupportedBrowser />}
      <CookieBanner />
    </>
  )
}
export default withImpersonation(Layout)
